import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';

interface TenantRow {
  id: string;
  name: string;
  status: 'active' | 'suspended' | 'archived';
}

interface BuildingResource {
  id: string;
  unifi_resource_type: 'door' | 'door_group' | 'device';
  name: string;
  full_name: string | null;
}

interface BuildingSchedule {
  id: string;
  name: string;
}

interface AccessZone {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  schedule_id: string | null;
  schedule: BuildingSchedule | null;
  resources: Array<{
    building_resource_id: string;
    building_resource: BuildingResource;
  }>;
  tenant_assignments: Array<{
    tenant_id: string;
    tenant: { id: string; name: string; status?: TenantRow['status'] };
  }>;
}

async function loadAccessZonePageData() {
  const [zones, resources, schedules, tenants] = await Promise.all([
    apiClient.get<{ data: AccessZone[] }>('/api/v1/admin/access-zones', {
      skipTenantScope: true,
    }),
    apiClient.get<{ data: BuildingResource[] }>('/api/v1/admin/hardware/resources', {
      skipTenantScope: true,
    }),
    apiClient.get<{ data: BuildingSchedule[] }>('/api/v1/admin/schedules', {
      skipTenantScope: true,
    }),
    apiClient.get<{ data: TenantRow[] }>('/api/v1/tenants', { skipTenantScope: true }),
  ]);
  return {
    zones: zones.data,
    resources: resources.data,
    schedules: schedules.data,
    tenants: tenants.data,
  };
}

type ModalState =
  | {
      kind: 'create';
      name: string;
      description: string;
      resourceIds: string[];
      scheduleId: string;
    }
  | {
      kind: 'edit';
      zone: AccessZone;
      name: string;
      description: string;
      resourceIds: string[];
      scheduleId: string;
    }
  | { kind: 'assign'; zone: AccessZone; tenantIds: string[] };

export function AdminAccessZonesPage() {
  const [zones, setZones] = useState<AccessZone[] | null>(null);
  const [resources, setResources] = useState<BuildingResource[]>([]);
  const [schedules, setSchedules] = useState<BuildingSchedule[]>([]);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [busy, setBusy] = useState(false);

  const activeTenants = useMemo(() => tenants.filter((tenant) => tenant.status === 'active'), [
    tenants,
  ]);
  const doorResources = useMemo(
    () => resources.filter((resource) => resource.unifi_resource_type !== 'device'),
    [resources],
  );

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await loadAccessZonePageData();
      setZones(data.zones);
      setResources(data.resources);
      setSchedules(data.schedules);
      setTenants(data.tenants);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load Access Zones');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function createZone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!modal || modal.kind !== 'create') return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(
        '/api/v1/admin/access-zones',
        {
          name: modal.name.trim(),
          description: modal.description.trim() || null,
          schedule_id: modal.scheduleId || null,
          resource_ids: modal.resourceIds,
        },
        { skipTenantScope: true },
      );
      setToast(`Created ${modal.name.trim()}.`);
      setModal(null);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create Access Zone');
    } finally {
      setBusy(false);
    }
  }

  async function updateZone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!modal || modal.kind !== 'edit') return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.patch(
        `/api/v1/admin/access-zones/${modal.zone.id}`,
        {
          name: modal.name.trim(),
          description: modal.description.trim() || null,
          schedule_id: modal.scheduleId || null,
        },
        { skipTenantScope: true },
      );
      const originalResourceIds = modal.zone.resources.map(
        (resource) => resource.building_resource_id,
      );
      const resourceIdsChanged =
        originalResourceIds.length !== modal.resourceIds.length ||
        originalResourceIds.some((id) => !modal.resourceIds.includes(id));
      if (resourceIdsChanged) {
        await apiClient.put(
          `/api/v1/admin/access-zones/${modal.zone.id}/resources`,
          { resource_ids: modal.resourceIds },
          { skipTenantScope: true },
        );
      }
      setToast(`Updated ${modal.name.trim()}.`);
      setModal(null);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update Access Zone');
    } finally {
      setBusy(false);
    }
  }

  async function assignZone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!modal || modal.kind !== 'assign') return;
    setBusy(true);
    setError(null);
    try {
      for (const tenantId of modal.tenantIds) {
        await apiClient.post(
          `/api/v1/admin/access-zones/${modal.zone.id}/tenants`,
          { tenant_id: tenantId },
          { skipTenantScope: true },
        );
      }
      setToast(
        `Assigned ${modal.zone.name} to ${modal.tenantIds.length} tenant${
          modal.tenantIds.length === 1 ? '' : 's'
        }.`,
      );
      setModal(null);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to assign Access Zone');
    } finally {
      setBusy(false);
    }
  }

  async function removeAssignment(zone: AccessZone, tenantId: string) {
    setBusy(true);
    setError(null);
    try {
      await apiClient.delete(`/api/v1/admin/access-zones/${zone.id}/tenants/${tenantId}`, {
        skipTenantScope: true,
      });
      setToast(`Removed ${zone.name}.`);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to remove Access Zone assignment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary-container text-on-primary">
            <Icon name="hub" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Access Zones
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Building-owned groups of doors that can be assigned to one or more tenants.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Link
            to="/hardware/claim"
            className="inline-flex items-center gap-2 rounded-lg border border-outline-variant px-4 py-2 font-label-md text-label-md text-on-surface hover:bg-surface-container"
          >
            <Icon name="door_front" className="text-base" />
            Door Permissions
          </Link>
          <Button
            onClick={() =>
              setModal({
                kind: 'create',
                name: '',
                description: '',
                resourceIds: [],
                scheduleId: '',
              })
            }
          >
            <Icon name="add" className="mr-1 text-sm" />
            New zone
          </Button>
        </div>
      </header>

      {toast ? (
        <div
          role="status"
          className="flex items-center gap-2 rounded-lg bg-secondary-container px-4 py-2 text-on-secondary-container"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" className="underline" onClick={() => setToast(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {error ? (
        <div role="alert" className="rounded-lg bg-error-container px-4 py-3 text-on-error-container">
          {error}
        </div>
      ) : null}

      <section className="overflow-hidden rounded-xl border border-outline-variant bg-surface-container-lowest">
        {zones == null ? (
          <div className="flex justify-center p-12">
            <Spinner />
          </div>
        ) : zones.length === 0 ? (
          <div className="p-12 text-center font-body-md text-body-md text-on-surface-variant">
            No Access Zones yet. Create Common Areas after refreshing hardware inventory.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-container">
              <tr>
                <Th>Zone</Th>
                <Th>Doors</Th>
                <Th>Schedule</Th>
                <Th>Assigned tenants</Th>
                <Th align="right">&nbsp;</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {zones.map((zone) => (
                <tr key={zone.id} className="hover:bg-surface-container-low">
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <span className="font-body-md text-body-md text-on-surface">{zone.name}</span>
                      {!zone.enabled ? <Badge tone="neutral">Disabled</Badge> : null}
                    </div>
                    {zone.description ? (
                      <div className="font-body-sm text-body-sm text-on-surface-variant">
                        {zone.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex max-w-xl flex-wrap gap-2">
                      {zone.resources.length === 0 ? (
                        <span className="text-on-surface-variant">No doors selected</span>
                      ) : (
                        zone.resources.map((resource) => (
                          <Badge key={resource.building_resource_id} tone="neutral">
                            {resourceLabel(resource.building_resource)}
                          </Badge>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {zone.schedule ? (
                      <Badge tone="neutral">{zone.schedule.name}</Badge>
                    ) : (
                      <span className="text-on-surface-variant">No schedule</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-wrap gap-2">
                      {zone.tenant_assignments.length === 0 ? (
                        <span className="text-on-surface-variant">No tenants assigned</span>
                      ) : (
                        zone.tenant_assignments.map((assignment) => (
                          <span
                            key={assignment.tenant_id}
                            className="inline-flex items-center gap-2 rounded-full bg-surface-container px-3 py-1 font-body-sm text-body-sm"
                          >
                            {assignment.tenant.name}
                            <button
                              type="button"
                              className="text-error hover:underline"
                              disabled={busy}
                              onClick={() => void removeAssignment(zone, assignment.tenant_id)}
                            >
                              Remove
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right align-top">
                    <div className="inline-flex flex-wrap justify-end gap-2">
                      <Button
                        variant="secondary"
                        disabled={busy}
                        aria-label={`Edit ${zone.name}`}
                        onClick={() =>
                          setModal({
                            kind: 'edit',
                            zone,
                            name: zone.name,
                            description: zone.description ?? '',
                            resourceIds: zone.resources.map(
                              (resource) => resource.building_resource_id,
                            ),
                            scheduleId: zone.schedule_id ?? '',
                          })
                        }
                      >
                        <Icon name="edit" className="mr-1 text-sm" />
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={busy || activeTenants.length === 0}
                        onClick={() =>
                          setModal({
                            kind: 'assign',
                            zone,
                            tenantIds: [],
                          })
                        }
                      >
                        <Icon name="group_add" className="mr-1 text-sm" />
                        Assign
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {modal?.kind === 'create' ? (
        <ModalShell title="New Access Zone" icon="add">
          <form onSubmit={createZone} className="space-y-4">
            <Field label="Zone name" htmlFor="zone-name">
              <input
                id="zone-name"
                required
                value={modal.name}
                onChange={(e) =>
                  setModal((m) => (m?.kind === 'create' ? { ...m, name: e.target.value } : m))
                }
                className={fieldCls}
                placeholder="Common Areas"
              />
            </Field>
            <Field label="Description" htmlFor="zone-description">
              <input
                id="zone-description"
                value={modal.description}
                onChange={(e) =>
                  setModal((m) =>
                    m?.kind === 'create' ? { ...m, description: e.target.value } : m,
                  )
                }
                className={fieldCls}
                placeholder="Lobby, shared entries, elevators"
              />
            </Field>
            <ScheduleField
              value={modal.scheduleId}
              schedules={schedules}
              onChange={(scheduleId) =>
                setModal((m) =>
                  m?.kind === 'create' ? { ...m, scheduleId } : m,
                )
              }
            />
            <fieldset className="space-y-2">
              <legend className="font-label-md text-label-md text-on-surface">Doors</legend>
              <div className="max-h-72 overflow-auto rounded-lg border border-outline-variant divide-y divide-outline-variant">
                {doorResources.length === 0 ? (
                  <div className="px-3 py-2 text-on-surface-variant">
                    No doors found. Refresh inventory from Door Permissions.
                  </div>
                ) : (
                  doorResources.map((resource) => {
                    const label = resourceLabel(resource);
                    const checked = modal.resourceIds.includes(resource.id);
                    return (
                      <label key={resource.id} className="flex items-center gap-3 px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={label}
                          checked={checked}
                          onChange={(e) =>
                            setModal((m) => {
                              if (m?.kind !== 'create') return m;
                              return {
                                ...m,
                                resourceIds: e.target.checked
                                  ? [...m.resourceIds, resource.id]
                                  : m.resourceIds.filter((id) => id !== resource.id),
                              };
                            })
                          }
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </fieldset>
            <ModalActions
              busy={busy}
              submitLabel="Create zone"
              onCancel={() => setModal(null)}
            />
          </form>
        </ModalShell>
      ) : null}

      {modal?.kind === 'edit' ? (
        <ModalShell title={`Edit ${modal.zone.name}`} icon="edit">
          <form onSubmit={updateZone} className="space-y-4">
            <Field label="Zone name" htmlFor="zone-edit-name">
              <input
                id="zone-edit-name"
                required
                value={modal.name}
                onChange={(e) =>
                  setModal((m) =>
                    m?.kind === 'edit' ? { ...m, name: e.target.value } : m,
                  )
                }
                className={fieldCls}
              />
            </Field>
            <Field label="Description" htmlFor="zone-edit-description">
              <input
                id="zone-edit-description"
                value={modal.description}
                onChange={(e) =>
                  setModal((m) =>
                    m?.kind === 'edit'
                      ? { ...m, description: e.target.value }
                      : m,
                  )
                }
                className={fieldCls}
              />
            </Field>
            <ScheduleField
              value={modal.scheduleId}
              schedules={schedules}
              onChange={(scheduleId) =>
                setModal((m) =>
                  m?.kind === 'edit' ? { ...m, scheduleId } : m,
                )
              }
            />
            <fieldset className="space-y-2">
              <legend className="font-label-md text-label-md text-on-surface">Doors</legend>
              <div className="max-h-72 overflow-auto rounded-lg border border-outline-variant divide-y divide-outline-variant">
                {doorResources.length === 0 ? (
                  <div className="px-3 py-2 text-on-surface-variant">
                    No doors found. Refresh inventory from Door Permissions.
                  </div>
                ) : (
                  doorResources.map((resource) => {
                    const label = resourceLabel(resource);
                    const checked = modal.resourceIds.includes(resource.id);
                    return (
                      <label key={resource.id} className="flex items-center gap-3 px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={label}
                          checked={checked}
                          onChange={(e) =>
                            setModal((m) => {
                              if (m?.kind !== 'edit') return m;
                              return {
                                ...m,
                                resourceIds: e.target.checked
                                  ? [...m.resourceIds, resource.id]
                                  : m.resourceIds.filter((id) => id !== resource.id),
                              };
                            })
                          }
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </fieldset>
            <ModalActions busy={busy} submitLabel="Save zone" onCancel={() => setModal(null)} />
          </form>
        </ModalShell>
      ) : null}

      {modal?.kind === 'assign' ? (
        <ModalShell title={`Assign ${modal.zone.name}`} icon="group_add">
          <form onSubmit={assignZone} className="space-y-4">
            <div className="space-y-2">
              <div className="font-label-md text-label-md text-on-surface">Tenants</div>
              <div className="grid max-h-80 gap-2 overflow-y-auto rounded-lg border border-outline-variant p-2">
                {activeTenants
                  .filter(
                    (tenant) =>
                      !modal.zone.tenant_assignments.some(
                        (assignment) => assignment.tenant_id === tenant.id,
                      ),
                  )
                  .map((tenant) => {
                    const selected = modal.tenantIds.includes(tenant.id);
                    return (
                      <button
                        key={tenant.id}
                        type="button"
                        onClick={() =>
                          setModal((m) => {
                            if (m?.kind !== 'assign') return m;
                            return {
                              ...m,
                              tenantIds: selected
                                ? m.tenantIds.filter((id) => id !== tenant.id)
                                : [...m.tenantIds, tenant.id],
                            };
                          })
                        }
                        className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left ${
                          selected
                            ? 'border-primary bg-primary-container text-on-primary-container'
                            : 'border-outline-variant bg-surface text-on-surface'
                        }`}
                      >
                        <span>{tenant.name}</span>
                        {selected ? <Icon name="check" className="text-base" /> : null}
                      </button>
                    );
                  })}
              </div>
              {activeTenants.every((tenant) =>
                modal.zone.tenant_assignments.some(
                  (assignment) => assignment.tenant_id === tenant.id,
                ),
              ) ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  All active tenants are already assigned.
                </p>
              ) : (
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  Select one or more tenants to assign.
                </p>
              )}
            </div>
            <ModalActions
              busy={busy}
              submitLabel="Assign selected"
              submitDisabled={modal.tenantIds.length === 0}
              onCancel={() => setModal(null)}
            />
          </form>
        </ModalShell>
      ) : null}
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`${align === 'right' ? 'text-right' : 'text-left'} px-4 py-3 font-label-md text-label-md uppercase text-on-surface-variant`}
    >
      {children}
    </th>
  );
}

function compactResourceName(value: string): string {
  const parts = value
    .split(/\s+-\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 3) return parts.slice(2).join(' - ');
  return value;
}

function resourceLabel(resource: BuildingResource): string {
  return compactResourceName(resource.full_name ?? resource.name);
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="block font-label-md text-label-md text-on-surface">
        {label}
      </label>
      {children}
    </div>
  );
}

function ScheduleField({
  value,
  schedules,
  onChange,
}: {
  value: string;
  schedules: BuildingSchedule[];
  onChange: (scheduleId: string) => void;
}) {
  return (
    <Field label="Schedule" htmlFor="zone-schedule">
      <select
        id="zone-schedule"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={fieldCls}
      >
        <option value="">No schedule</option>
        {schedules.map((schedule) => (
          <option key={schedule.id} value={schedule.id}>
            {schedule.name}
          </option>
        ))}
      </select>
    </Field>
  );
}

function ModalShell({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-labelledby="access-zone-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
    >
      <div className="max-h-[90vh] w-full max-w-[640px] overflow-y-auto rounded-xl border border-outline-variant bg-surface-container-lowest p-8 shadow-lg">
        <header className="mb-6 flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-container text-on-primary">
            <Icon name={icon} />
          </div>
          <h2 id="access-zone-modal-title" className="font-headline-sm text-headline-sm">
            {title}
          </h2>
        </header>
        {children}
      </div>
    </div>
  );
}

function ModalActions({
  busy,
  submitLabel,
  submitDisabled,
  onCancel,
}: {
  busy: boolean;
  submitLabel: string;
  submitDisabled?: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      <Button type="submit" loading={busy} disabled={submitDisabled}>
        {submitLabel}
      </Button>
    </div>
  );
}

const fieldCls =
  'w-full rounded-lg border border-outline-variant bg-surface px-3 py-2 font-body-md text-body-md outline-none focus:ring-2 focus:ring-primary';
